import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import {
  FormsModule,
  ReactiveFormsModule,
  UntypedFormArray,
  UntypedFormControl,
  UntypedFormGroup,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { SubscriptionTierInterface, SubscriptionTiersService } from '@galaxy/partner';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { And, RuleInterface, StringEquals } from '@vendasta/automata';
import { GalaxyAlertModule } from '@vendasta/galaxy/alert';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { GalaxyPipesModule } from '@vendasta/galaxy/pipes';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { Observable, of } from 'rxjs';
import { catchError, map, take, tap } from 'rxjs/operators';
import { CommonTriggerPanelComponent } from '../../../common/common-trigger-panel.component';
import { BillingFrequency, UpdateType } from './consts';

@Component({
  selector: 'automata-partner-subscription-update-panel',
  templateUrl: './partner-subscription-update-panel.component.html',
  imports: [
    CommonModule,
    TranslateModule,
    GalaxyAlertModule,
    MatButtonModule,
    MatCardModule,
    FormsModule,
    GalaxyFormFieldModule,
    ReactiveFormsModule,
    MatInputModule,
    MatIconModule,
    MatSelectModule,
    GalaxyPipesModule,
  ],
})
export class PartnerSubscriptionUpdatePanelComponent extends CommonTriggerPanelComponent implements OnInit {
  updateType = '*';
  fromSubscriptionTier = '*';
  toSubscriptionTier = '*';
  toBillingFrequency = '*';
  fromBillingFrequency = '*';
  updateTypeEnum = UpdateType;
  billingFrequencyEnum = BillingFrequency;
  subscriptionTierOptions$: Observable<{ name: string; value: string }[]>;

  constructor(
    protected readonly snackbarService: SnackbarService,
    protected readonly translateService: TranslateService,
    private subscriptionTiersService: SubscriptionTiersService,
  ) {
    super();
  }

  ngOnInit(): void {
    super.ngOnInit();
    if (this.triggerChoices?.rules?.length === 1) {
      const and = And.fromRuleInterface(this.triggerChoices.rules[0]);
      const updateTypeRule = and.value[0]?.value;
      const fromSubTierRule = and.value[1]?.value;
      const toSubTierRule = and.value[2]?.value;
      const toBillingFreqRule = and.value[3]?.value;
      const fromBillingFreqRule = and.value[4]?.value;

      this.updateType = updateTypeRule || '*';
      this.fromSubscriptionTier = fromSubTierRule || '*';
      this.toSubscriptionTier = toSubTierRule || '*';
      this.toBillingFrequency = toBillingFreqRule || '*';
      this.fromBillingFrequency = fromBillingFreqRule || '*';
    }

    this.subscriptionTierOptions$ = this.subscriptionTiersService.subscriptionTierList$.pipe(
      take(1),
      map((tiers: SubscriptionTierInterface[]) => {
        const subscriptionTierOptions = [];
        for (let i = 0; i < tiers.length; i++) {
          if (tiers[i].subscriptionUpdate) {
            subscriptionTierOptions.push({
              name: tiers[i].name + ' (' + tiers[i].tierVersion + ')',
              value: tiers[i].id,
            });
          }
        }
        return subscriptionTierOptions;
      }),
      tap(() => this.savingEnabled$.next(true)),
      catchError(() => {
        this.snackbarService.errorSnack(
          this.translateService.instant('AUTOMATIONS.EDITOR.TRIGGERS.PARTNER_SUBSCRIPTION_TIER_UPDATE.LOAD_ERROR'),
        );
        return of(null);
      }),
    );
  }

  getBaseRules(): RuleInterface[] {
    return [];
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  fillRuleValues(rules: RuleInterface[]): RuleInterface[] {
    const updateTypeRule = new StringEquals('{.update_type}', this.updateType);
    const fromSubscriptionTierRule = new StringEquals('{.from_subscription_tier_id}', this.fromSubscriptionTier);
    const toSubscriptionTierRule = new StringEquals('{.to_subscription_tier_id}', this.toSubscriptionTier);
    const toBillingFrequency = new StringEquals('{.to_billing_frequency}', this.toBillingFrequency);
    const fromBillingFrequency = new StringEquals('{.from_billing_frequency}', this.fromBillingFrequency);
    return [
      new And(
        [updateTypeRule, fromSubscriptionTierRule, toSubscriptionTierRule, toBillingFrequency, fromBillingFrequency],
        '',
      ).toRuleInterface(),
    ];
  }

  getControlsToValidateOnSave(): (UntypedFormControl | UntypedFormGroup | UntypedFormArray)[] {
    return [];
  }

  isFormValid(): boolean {
    return true;
  }
}
